import {
  DropdownDivider,
  DropdownItem,
  DropdownSubmenu,
  useTranslationContext,
} from 'platform/components';
import {Icon, Show} from 'platform/foundation';

import {useColumnMenuContext} from '../../../../../../../../context/ColumnMenuContext';
import {useIsMobileView} from '../../../../../../../../hooks/useIsMobileView';
import {useClipboardExportAllowed} from '../../../../../../../../store/selectors/useBehavior';
import {useCanCopyColumn} from '../../../../../../../../store/selectors/useCanCopyColumn';
import {PIN_OPTIONS} from './PIN_OPTIONS';
import {SORT_OPTIONS} from './SORT_OPTIONS';

type ColumnMenuOptionsTabProps = {
  currentPinned: 'LEFT' | 'RIGHT' | null | undefined;
  /** Key of the column this menu belongs to — used to call onAutosizeColumn / onPinColumn */
  columnKey: string;
  /** Whether the column supports sorting — controls Sort submenu visibility */
  columnSortable: boolean;
  /** Called after any action to close the parent popover */
  onClose: () => void;
};

/**
 * Options tab content for the column header menu.
 *
 * Renders: Sort submenu (if sortable), Pin column submenu (No pin / Pin left / Pin right),
 * Autosize this column, Autosize all columns, divider, Copy column (current page),
 * Reset columns, Reset filters.
 *
 * Visibility of pinning and autosize groups is controlled via ColumnMenuContext
 * (showPinning / showAutosize flags from the backend definition).
 * All action callbacks are also read from ColumnMenuContext.
 *
 * Must be rendered inside a <Dropdown> / <DropdownContainer> so that DropdownItem
 * receives the required MenuContext + FloatingList context from platform/components.
 */
export function ColumnMenuOptionsTab(props: ColumnMenuOptionsTabProps) {
  const t = useTranslationContext();
  const columnMenu = useColumnMenuContext();
  const isMobile = useIsMobileView();
  const clipboardExportAllowed = useClipboardExportAllowed();
  const canCopyColumn = useCanCopyColumn(props.columnKey);

  const normalizedPinned = props.currentPinned ?? null;
  const currentSort = columnMenu.columnStates.get(props.columnKey)?.sort ?? null;

  const handleSort = (sort: 'ASC' | 'DESC' | null) => {
    columnMenu.onSort(props.columnKey, sort);
    props.onClose();
  };

  const handlePin = (value: 'LEFT' | 'RIGHT' | null) => {
    columnMenu.onPinColumn(props.columnKey, value);
    props.onClose();
  };

  const handleAutosizeThis = () => {
    columnMenu.onAutosizeColumn(props.columnKey);
    props.onClose();
  };

  const handleAutosizeAll = () => {
    columnMenu.onAutosizeAllColumns();
    props.onClose();
  };

  const handleResetColumns = () => {
    columnMenu.onResetColumns();
    props.onClose();
  };

  const handleResetFilters = () => {
    columnMenu.onResetFilters();
    props.onClose();
  };

  const handleCopyColumn = () => {
    columnMenu.onCopyColumn(props.columnKey);
    props.onClose();
  };

  const renderSortItems = () =>
    SORT_OPTIONS.map((option) => (
      <DropdownItem
        key={option.value ?? 'none'}
        label={t(option.labelKey, {defaultValue: option.defaultLabel})}
        isActive={currentSort === option.value && option.value !== null}
        isDisabled={option.value === null && currentSort === null}
        onClick={() => handleSort(option.value)}
        data-testid={`columnMenuSortOption-${option.value ?? 'none'}`}
      />
    ));

  const renderPinItems = () =>
    PIN_OPTIONS.map((option) => (
      <DropdownItem
        key={option.value ?? 'none'}
        label={t(option.labelKey, {defaultValue: option.defaultLabel})}
        isActive={normalizedPinned === option.value}
        onClick={() => handlePin(option.value)}
        data-testid={`columnMenuPinOption-${option.value ?? 'none'}`}
      />
    ));

  return (
    <div className="p-2">
      {props.columnSortable &&
        (isMobile ? (
          // Mobile: flat DropdownItems — DropdownSubmenu creates an isolated FloatingTree
          // that breaks useDismiss on touch (pointerdown fires before click reaches the item).
          <>
            {renderSortItems()}
            <DropdownDivider />
          </>
        ) : (
          <DropdownSubmenu
            label={t('page.datagrid.labels.sort', {defaultValue: 'Sort'})}
            leftIcon="content/sort"
            isActive={currentSort !== null}
            suffix={<Icon value="navigation/arrow_right" />}
            placement="right-start"
            strategy="fixed"
            data-testid="columnMenuSortSubmenu"
          >
            {renderSortItems()}
          </DropdownSubmenu>
        ))}

      {columnMenu.showPinning &&
        (isMobile ? (
          // Mobile: flat DropdownItems for the same reason as Sort above
          <>
            {renderPinItems()}
            <DropdownDivider />
          </>
        ) : (
          <DropdownSubmenu
            label={t('page.datagrid.labels.pinColumn', {defaultValue: 'Pin column'})}
            leftIcon="custom/push_pin"
            isActive={normalizedPinned !== null}
            suffix={<Icon value="navigation/arrow_right" />}
            placement="right-start"
            strategy="fixed"
            data-testid="columnMenuPinSubmenu"
          >
            {renderPinItems()}
          </DropdownSubmenu>
        ))}

      <Show when={columnMenu.showAutosize}>
        <>
          <Show when={(props.columnSortable || columnMenu.showPinning) && !isMobile}>
            <DropdownDivider data-testid="columnMenuAutosizeDivider" />
          </Show>
          <DropdownItem
            label={t('page.datagrid.labels.autosizeThisCol', {
              defaultValue: 'Autosize this column',
            })}
            leftIcon="action/aspect_ratio"
            onClick={handleAutosizeThis}
            data-testid="columnMenuAutosizeThis"
          />
          <DropdownItem
            label={t('page.datagrid.labels.autosizeAllCols', {
              defaultValue: 'Autosize all columns',
            })}
            leftIcon="action/aspect_ratio"
            onClick={handleAutosizeAll}
            data-testid="columnMenuAutosizeAll"
          />
        </>
      </Show>

      <DropdownDivider data-testid="columnMenuDivider" />

      <Show when={clipboardExportAllowed}>
        <DropdownItem
          label={t('page.datagrid.labels.copyColumnCurrentPage', {
            defaultValue: 'Copy column (current page)',
          })}
          leftIcon="content/copy"
          isDisabled={!canCopyColumn}
          onClick={handleCopyColumn}
          data-testid="columnMenuCopyColumn"
        />
      </Show>

      <DropdownItem
        label={t('page.datagrid.labels.resetCols', {defaultValue: 'Reset columns'})}
        leftIcon="action/restore"
        onClick={handleResetColumns}
        data-testid="columnMenuResetColumns"
      />
      <DropdownItem
        label={t('page.datagrid.labels.resetAllFilters', {defaultValue: 'Reset filters'})}
        leftIcon="content/filter_list"
        onClick={handleResetFilters}
        data-testid="columnMenuResetFilters"
      />
    </div>
  );
}
